/**
 * Pure sync-scope helpers for the Integrations tab, extracted so the logic that
 * mirrors the backend `dispatch_mode` precedence is unit-testable without
 * rendering the component (mirrors the `integrationCredentials.js` pattern).
 */

/** Provider config keys that can carry a group selection. */
const SCOPED_PROVIDER_KEYS = ["entraId", "okta", "googleWorkspace"];

/** Whether this provider's scope can narrow to a group selection. All three do. */
export function supportsGroupScope(provider) {
    return provider === "ENTRA_ID" || provider === "OKTA" || provider === "GOOGLE";
}

/**
 * The sync type the Sync button should request, derived from a
 * GetDirectoryIntegration `config`. Mirrors the backend `dispatch_mode`
 * precedence exactly: whole-tenant wins (`"full"`); else a group selection
 * (`"scoped"`); else the integration isn't configured to sync yet (`null` →
 * nothing to send). Drift from the backend here silently produces confusing
 * 412s, so it's pinned by tests.
 */
export function resolveSyncType(config) {
    if (config?.syncFullTenant) return "full";
    const selected = SCOPED_PROVIDER_KEYS.some(
        (key) => (config?.[key]?.syncedGroupIds ?? []).length > 0,
    );
    return selected ? "scoped" : null;
}

/**
 * Whether the scope picker has a completing choice to save: whole-tenant (any
 * provider), or — where groups are selectable — at least one selected group.
 * Without one the integration stays unconfigured, so there's nothing to persist.
 */
export function canSaveScope({ syncFull, scopable, selectedCount }) {
    return syncFull || (scopable && selectedCount > 0);
}

/**
 * The provider config to persist from the picker, merged onto the loaded config
 * so connection-bound fields (tenantId/clientId, orgDomain, bindingNonce,
 * customerId) survive. A provider with no group selection carries its config
 * unchanged (its scope is the `sync_full_tenant` flag alone).
 *
 * A whole-tenant save **keeps** any group selection rather than clearing it.
 * `sync_full_tenant` already wins outright in `resolveSyncType` and in the
 * backend's dispatch precedence, so the retained ids change no behaviour — but
 * clearing them silently destroyed work: the picker greys out while still
 * rendering the boxes as checked, so the admin sees an intact selection, saves,
 * and has to rebuild it from a paginated live list to get back.
 */
export function scopeConfigPayload({ providerCfg, scopable, syncFull, selected }) {
    if (!scopable) return { ...providerCfg };
    return { ...providerCfg, syncedGroupIds: [...selected] };
}

/**
 * A comparable key for the scope that actually reaches dispatch, used to decide
 * whether a save needs a re-sync.
 *
 * Deliberately keyed off *effective* scope, not the stored config. Whole-tenant
 * wins outright, so editing the group list while it is on changes nothing about
 * what syncs — comparing raw config would resync for no reason. Order-insensitive
 * for the same reason: reordering ids is not a scope change.
 */
export function effectiveScopeKey({ syncFull, scopable, selected }) {
    if (syncFull) return "full";
    if (!scopable) return "none";
    const ids = [...selected].sort();
    return ids.length ? `scoped:${ids.join(",")}` : "none";
}
